<template>
  <div ref="dropdownElement" class="c-document-control">
    <button
      :class="['btn', 'c-document-control__button', { 'c-document-control__button--open': open }]"
      @click="toggleOpen"
    >
      {{ $t('DOCUMENT_CONTROL.MANAGE') }}
      <GripArrowDownIcon :class="['c-document-control__icon', { 'c-document-control__icon--reversed': open }]" />
    </button>
    <ul v-if="open" class="c-document-control__dropdown">
      <li class="c-document-control__list-item">
        <p class="c-document-control__name">{{ exercise.name || generateName(getGenreName(), 0, '') }}</p>
      </li>
      <li class="c-document-control__list-item">
        <button class="btn c-document-control__element" @click="openSaveAsModal">
          {{ $t('DOCUMENT_CONTROL.SAVE_AS') }}
          <GripArrowDownIcon class="c-document-control__icon c-document-control__icon--right" />
        </button>
      </li>
      <li class="c-document-control__list-item">
        <button class="btn c-document-control__element" @click="onClickSave">{{ $t('DOCUMENT_CONTROL.SAVE') }}</button>
      </li>
      <li class="c-document-control__list-item">
        <button class="btn c-document-control__element" @click="openRenameModal">
          {{ $t('DOCUMENT_CONTROL.RENAME') }}
          <GripArrowDownIcon class="c-document-control__icon c-document-control__icon--right" />
        </button>
      </li>
      <li class="c-document-control__list-item">
        <button class="btn c-document-control__element" @click="openRemoveModal">
          {{ $t('DOCUMENT_CONTROL.DELETE') }}
        </button>
      </li>
      <li class="c-document-control__list-item c-document-control__list-item--close">
        <button class="btn c-document-control__element c-document-control__element--close" @click="toggleOpen">
          <CloseIcon class="c-document-control__close-icon" />
          {{ $t('DOCUMENT_CONTROL.CLOSE') }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import GripArrowDownIcon from '@/assets/icons/GripArrowDownIcon.vue';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import ChangeModal from '@/components/document-control/ChangeModal.vue';
import RemoveModal from '@/components/document-control/RemoveModal.vue';
import useModal from '@/services/useModal';
import useExercises from '@/services/useExercises';
import useGenre from '@/services/useGenre';
import { useRoute, useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';

const open = ref<boolean>(false);
const newName = ref<string>('');
const modal = useModal();
const dropdownElement = ref(null);
const {
  params: { slug },
} = useRoute();
const router = useRouter();
const { genres } = useGenre();
const { save, saveAs, removeById, exercise, generateExerciseLink, generateName } = useExercises();

const toggleOpen = () => (open.value = !open.value);

const openRenameModal = () => {
  toggleOpen();
  modal.open(ChangeModal, { modelValue: exercise.value.name }, { 'update:modelValue': updateName, save: onSave });
};

const openRemoveModal = () => {
  toggleOpen();
  modal.open(RemoveModal, {}, { remove: onRemove });
};

const openSaveAsModal = () => {
  toggleOpen();
  modal.open(
    ChangeModal,
    { modelValue: exercise.value.name, rename: false },
    { 'update:modelValue': updateNewName, saveAs: onSaveAs },
  );
};

const getGenreName = () => genres.value.find((genre) => genre.slug === exercise.value.genre).name;

const updateName = (name) => {
  exercise.value.name = name;
};

const onSave = async () => {
  await save(exercise.value);
};

const updateNewName = (newNameValue) => {
  newName.value = newNameValue;
};

const onSaveAs = async () => {
  const newExercise = JSON.parse(JSON.stringify(exercise.value));
  const exerciseCopy = await saveAs({ ...newExercise, name: newName.value }, slug as string);
  await router.push({ path: generateExerciseLink(slug as string, exerciseCopy.version) });
};

const onRemove = async () => {
  await router.push({ path: '/' });
  await removeById(exercise.value._id);
};

const onClickSave = async () => {
  await onSave();
  toggleOpen();
};

onClickOutside(dropdownElement, () => {
  if (open.value) {
    toggleOpen();
  }
});
</script>
